import React, { useState, useCallback, useRef, useEffect } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import type { UploadTask } from 'firebase/storage';
import { storage } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Upload, X, Video, Package, Image } from 'lucide-react';

export type FileType = 'video' | 'audio' | 'project' | 'archive' | 'any' | 'image';

interface FirebaseStorageUploadProps {
  storagePath: string;
  fileType: FileType;
  onUploadComplete: (downloadURL: string, originalFileName?: string) => void;
  onUploadError: (error: Error) => void;
  onProgress?: (progress: number) => void;
  allowedTypes?: string[];
  maxFileSize?: number;
  hasExistingFile?: boolean;
  disabled?: boolean;
  className?: string;
  label?: string;
  hideIcon?: boolean;
}

interface UploadState {
  isUploading: boolean;
  progress: number;
  isPaused: boolean;
  uploadTask: UploadTask | null;
  error: string | null;
  uploadSpeed: number;
  estimatedTimeRemaining: number;
}

const DEFAULT_FILE_LIMITS = {
  video: 500 * 1024 * 1024, // 500MB
  audio: 50 * 1024 * 1024, // 50MB
  project: 100 * 1024 * 1024, // 100MB
  archive: 100 * 1024 * 1024, // 100MB
  image: 5 * 1024 * 1024, // 5MB
  any: 100 * 1024 * 1024, // 100MB
};

const DEFAULT_ALLOWED_TYPES = {
  video: ['.mp4', '.webm', '.mov', '.avi', '.qt'],
  audio: ['.wav', '.mp3', '.aac', '.m4a'],
  project: ['.zip', '.alp', '.rar', '.7z'],
  archive: ['.zip', '.rar', '.7z'],
  image: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  any: [], // No restrictions
};

export function FirebaseStorageUpload({
  storagePath,
  fileType,
  onUploadComplete,
  onUploadError,
  onProgress,
  allowedTypes,
  maxFileSize,
  hasExistingFile = false,
  disabled = false,
  className = '',
  label,
  hideIcon = false,
}: FirebaseStorageUploadProps) {
  const [uploadState, setUploadState] = useState<UploadState>({
    isUploading: false,
    progress: 0,
    isPaused: false,
    uploadTask: null,
    error: null,
    uploadSpeed: 0,
    estimatedTimeRemaining: 0,
  });

  const lastUpdateRef = useRef<number>(0);
  const lastBytesRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTaskRef = useRef<UploadTask | null>(null);
  const uploadCleanupRef = useRef<(() => void) | null>(null);

  // Cleanup upload task and listeners on component unmount
  useEffect(() => {
    const fileInput = fileInputRef.current;
    return () => {
      uploadCleanupRef.current?.();
      uploadTaskRef.current?.cancel();
      uploadTaskRef.current = null;
      if (fileInput) {
        fileInput.value = '';
      }
    };
  }, []);

  const validateFile = useCallback(
    (file: File) => {
      const maxSize = maxFileSize || DEFAULT_FILE_LIMITS[fileType] || DEFAULT_FILE_LIMITS.any;
      const allowedMimeTypes = allowedTypes || DEFAULT_ALLOWED_TYPES[fileType] || [];

      if (file.size > maxSize) {
        const maxSizeMB = Math.round(maxSize / (1024 * 1024));
        throw new Error(`File too large. Maximum size: ${maxSizeMB}MB`);
      }

      if (allowedMimeTypes.length > 0 && fileType !== 'any') {
        // Simple MIME check for images
        if (fileType === 'image') {
          if (!allowedMimeTypes.includes(file.type)) {
            throw new Error(`Invalid file type. Allowed types: ${allowedMimeTypes.join(', ')}`);
          }
        }
        // For extensions, we'd need more complex logic, but for now relying on accept attribute + basic checks
      }
    },
    [fileType, allowedTypes, maxFileSize]
  );

  const handleUpload = useCallback(
    async (file: File) => {
      try {
        validateFile(file);

        uploadCleanupRef.current?.();
        uploadTaskRef.current?.cancel();
        uploadCleanupRef.current = null;
        uploadTaskRef.current = null;

        setUploadState((prev) => ({
          ...prev,
          isUploading: true,
          progress: 0,
          error: null,
          uploadSpeed: 0,
          estimatedTimeRemaining: 0,
        }));

        startTimeRef.current = Date.now();
        lastUpdateRef.current = Date.now();
        lastBytesRef.current = 0;

        // Create storage path with file extension to preserve file type
        const fileExtension = file.name.substring(file.name.lastIndexOf('.'));
        const storagePathWithExtension = `${storagePath}${fileExtension}`;

        const storageRef = ref(storage, storagePathWithExtension);
        const uploadTask = uploadBytesResumable(storageRef, file);
        uploadTaskRef.current = uploadTask;

        setUploadState((prev) => ({ ...prev, uploadTask }));

        const unsubscribe = uploadTask.on(
          'state_changed',
          (snapshot) => {
            const now = Date.now();
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;

            const timeDelta = now - lastUpdateRef.current;
            const bytesDelta = snapshot.bytesTransferred - lastBytesRef.current;

            let uploadSpeed = 0;
            if (timeDelta > 500 && bytesDelta > 0) {
              uploadSpeed = (bytesDelta / timeDelta) * 1000;
              lastUpdateRef.current = now;
              lastBytesRef.current = snapshot.bytesTransferred;
            }

            const remainingBytes = snapshot.totalBytes - snapshot.bytesTransferred;
            const estimatedTimeRemaining = uploadSpeed > 0 ? remainingBytes / uploadSpeed : 0;

            setUploadState((prev) => ({
              ...prev,
              progress,
              uploadSpeed: uploadSpeed > 0 ? uploadSpeed : prev.uploadSpeed,
              estimatedTimeRemaining:
                estimatedTimeRemaining > 0 ? estimatedTimeRemaining : prev.estimatedTimeRemaining,
            }));

            onProgress?.(progress);
          },
          (error) => {
            uploadCleanupRef.current?.();
            uploadCleanupRef.current = null;
            uploadTaskRef.current = null;
            setUploadState((prev) => ({
              ...prev,
              isUploading: false,
              error: error.message,
            }));
            onUploadError(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              uploadCleanupRef.current?.();
              uploadCleanupRef.current = null;
              uploadTaskRef.current = null;
              setUploadState((prev) => ({
                ...prev,
                isUploading: false,
                progress: 100,
              }));
              onUploadComplete(downloadURL, file.name);
            } catch (error) {
              uploadCleanupRef.current?.();
              uploadCleanupRef.current = null;
              uploadTaskRef.current = null;
              setUploadState((prev) => ({
                ...prev,
                isUploading: false,
                error: (error as Error).message,
              }));
              onUploadError(error as Error);
            }
          }
        );

        uploadCleanupRef.current = unsubscribe;
      } catch (error) {
        setUploadState((prev) => ({
          ...prev,
          isUploading: false,
          error: (error as Error).message,
        }));
        onUploadError(error as Error);
      }
    },
    [storagePath, validateFile, onUploadComplete, onUploadError, onProgress]
  );

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        handleUpload(file);
      }
      event.target.value = '';
    },
    [handleUpload]
  );

  const handleButtonClick = useCallback(
    (event: React.MouseEvent) => {
      if (uploadState.isUploading) return;
      event.preventDefault();
      event.stopPropagation();
      fileInputRef.current?.click();
    },
    [uploadState.isUploading]
  );

  const handleCancel = useCallback(() => {
    if (uploadState.uploadTask) {
      uploadState.uploadTask.cancel();
      uploadCleanupRef.current?.();
      uploadCleanupRef.current = null;
      uploadTaskRef.current = null;
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setUploadState((prev) => ({
        ...prev,
        isUploading: false,
        isPaused: false,
        uploadTask: null,
        progress: 0,
        uploadSpeed: 0,
        estimatedTimeRemaining: 0,
        error: null,
      }));
    }
  }, [uploadState.uploadTask]);

  const getIcon = () => {
    switch (fileType) {
      case 'video':
        return Video;
      case 'image':
        return Image;
      case 'project':
      case 'archive':
        return Package;
      default:
        return Upload;
    }
  };

  const Icon = getIcon();
  const displayText = label || (hasExistingFile ? 'Replace File' : 'Upload File');

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          disabled={disabled || uploadState.isUploading}
          className="hidden"
          accept={allowedTypes?.join(',') || (fileType === 'image' ? 'image/*' : '*')}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled || uploadState.isUploading}
          onClick={handleButtonClick}
          className="h-12 flex items-center gap-2 px-4 w-full"
        >
          {!hideIcon && <Icon className="w-4 h-4" />}
          {uploadState.isUploading ? 'Uploading...' : displayText}
        </Button>

        {uploadState.isUploading && (
          <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
            <Button variant="ghost" size="icon" onClick={handleCancel}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>

      {uploadState.isUploading && (
        <div className="space-y-2">
          <Progress value={uploadState.progress} className="w-full" />
          <div className="flex justify-between items-center text-xs text-muted-foreground">
            <span className="flex-shrink-0">{Math.round(uploadState.progress)}%</span>
            {uploadState.uploadSpeed > 0 && (
              <span>{(uploadState.uploadSpeed / 1024 / 1024).toFixed(1)} MB/s</span>
            )}
          </div>
        </div>
      )}

      {uploadState.error && (
        <div className="text-sm text-destructive bg-destructive/10 p-2 rounded">
          {uploadState.error}
        </div>
      )}
    </div>
  );
}
